import {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import {
  useNavigate, NavigateFunction, useSearchParams,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import styled from 'styled-components';
import classNames from 'classnames';

import { actions } from 'Slice/AdvanceSearchSlice';
import {
  selectAdvanceSearchLoading,
  selectAdvanceSearchLoaded,
  selectAdvanceSearchIsPagination,
  selectAdvanceSearchPersonExtendedLoading,
  selectAdvanceSearchPersonExtendedLoaded,
  selectAdvanceSearchPeopleSection,
  selectAdvanceSearchCompaniesSection,
  selectAdvanceSearchBuyerGroupsSection,
  selectAdvanceSearchParams,
} from 'Selector/AdvanceSearchSelector';
import { selectViewTab } from 'Selector/UISelector';
import { selectShowGlobalGilroy } from 'Selector/GlobalSearchContentSelector';
import { AdvanceSearchType, AdvanceSearchRequest } from 'Types/AdvanceSearchResultsTypes';

import AccountPopOver from 'Components/common/components/AccountPopOver';
import SubNavBarPrimary from 'Components/layout/headers/SubNavBarPrimary';
import ViewTabs from 'Components/common/tabs/ViewTabs';
import SearchNoResultFoundWidget from 'Components/common/components/SearchNoResultFoundWidget';
import SearchPersonSkeletonBlock from 'Components/common/skeleton/SearchPersonSkeletonBlock';
import SearchCompanySkeletonBlock from 'Components/common/skeleton/SearchCompanySkeletonBlock';
import SearchBuyerGroupSkeletonBlock from 'Components/common/skeleton/SearchBuyerGroupSkeltonBlock';
import PeopleSection from 'Components/common/components/PeopleSection';
import PeopleSectionListView from 'Components/common/components/PeopleSectionListView';
import CompaniesSection from 'Components/common/components/CompaniesSection';
import CompaniesSectionListView from 'Components/common/components/CompaniesSectionListView';
import BuyerGroupsSection from 'Components/common/components/BuyerGroupsSection';
import BuyerGroupsSectionListView from 'Components/common/components/BuyerGroupsSectionListView';
import DrawerAlerts from 'Components/alerts/components/DrawerAlerts';
import DrawerPeopleGroup from 'Components/people/components/DrawerPeopleGroup';
import { actions as groupActions } from 'Slice/GroupsSlice';

import NavBarPrimary from 'Components/layout/headers/NavBarPrimary';
import BackIcon from 'Components/common/assets/svgIcons/BackIcon';
import GlobalSearch from 'Components/globalSearch/components/GlobalSearch';
import { Gilroy } from 'gilroy-global';
import { useGilroy } from 'Utils/hooks/useGilroy';
import { IsTablet } from 'Utils/UtilityFunctions';
import { ViewInterface } from 'Types/GlobalTypes';
import { FEED_GILROY_ROUTE } from 'Utils/Constants';
import {
  maxTablet,
  primaryFont,
  SecondaryColor,
  wMedium,
  smMobile,
} from 'Utils/utilsStyle';

const Wrapper = styled.div`
  .M-Buyer,
  .M-Buyer .infinite-scroll-component {
    overflow-x: hidden !important;
  }
  .M-Buyer::-webkit-scrollbar,
  .M-Buyer .infinite-scroll-component::-webkit-scrollbar {
    height: 0px;
  }
  .M-Buyer {
    scrollbar-width: none;
  }
  .container {
    margin-bottom: 100px;
    @media all and (max-width: ${maxTablet}) {
      margin-bottom: 0px;
    }
  .Grid-width-row.gridUpdated {
    padding-top: 14px;
    @media all and (max-width: ${maxTablet}) {
      padding-top: 0px;
    }
    }
  }
  .heading-inner-page .count {
    color: ${SecondaryColor};
    font-family: ${primaryFont};
    font-weight: ${wMedium};
    font-size: 18px;
    @media all and (max-width: ${maxTablet}) {
      font-size: 16px;
    }
  }
  .person-item {
    margin-bottom: 0px;
  }
  .small-button-chinese {
    margin-bottom: 0px;
  }
  .M-companies .companies-margin {
    .ant-row {
      &.Grid-width-row {
        &.col-six-fix-md {
          margin-top: 0px;
        }
      }
    }
  }
  .company-image-parent {
    @media all and (max-width: ${smMobile}) {
      margin-bottom: 4px;
    }
  }
  .searchHeadings {
    display: none;
  }
  .search-heading.sub-header-main-wrapper .sub-header-wrapper {
    padding: 14px 0 0 0;
  }
`;

const SIGNATURE_FIELDS: (keyof AdvanceSearchRequest)[] = [
  'searchType', 'fullName', 'designation', 'currentCompany', 'pastCompany',
  'name', 'industry', 'revenue', 'size', 'country', 'region',
];

const getRequestSignature = (req?: AdvanceSearchRequest | null): string => {
  if (!req?.searchType) return '';
  return SIGNATURE_FIELDS.map((field) => `${field}=${req[field] ?? ''}`).join('&');
};

const AdvanceSearchResults = function AdvanceSearchResults() {
  const dispatch = useDispatch();
  const navigate: NavigateFunction = useNavigate();
  const { gilroyStates, gilroyActions } = useGilroy();
  const [searchParams] = useSearchParams();
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [dragging, setDragging] = useState(false);

  const enableBackButton = true;

  const handleBackClick = () => {
    navigate(-1);
  };

  const navRequest = useMemo((): AdvanceSearchRequest | undefined => {
    const searchType = searchParams.get('searchType') as AdvanceSearchType | null;
    if (!searchType) return undefined;
    const req: AdvanceSearchRequest = { searchType };
    const fields = [
      'fullName', 'designation', 'currentCompany', 'pastCompany',
      'name', 'industry', 'revenue', 'size', 'country', 'region',
      'member', 'sharedBy',
    ] as const;
    fields.forEach((field) => {
      const val = searchParams.get(field);
      if (val) (req as unknown as Record<string, string>)[field] = val;
    });
    return req;
  }, [searchParams]);

  const isLoading = useSelector(selectAdvanceSearchLoading);
  const isLoaded = useSelector(selectAdvanceSearchLoaded);
  const isPaginating = useSelector(selectAdvanceSearchIsPagination);
  const isPersonExtendedLoading = useSelector(selectAdvanceSearchPersonExtendedLoading);
  const isPersonExtendedLoaded = useSelector(selectAdvanceSearchPersonExtendedLoaded);
  const peopleSection = useSelector(selectAdvanceSearchPeopleSection);
  const companiesSection = useSelector(selectAdvanceSearchCompaniesSection);
  const buyerGroupsSection = useSelector(selectAdvanceSearchBuyerGroupsSection);
  const storedParams = useSelector(selectAdvanceSearchParams);
  const tabView: ViewInterface = useSelector(selectViewTab);
  const isListView = tabView.view === 'listView';
  const showGlobalGilroy = useSelector(selectShowGlobalGilroy);

  const activeSearchType: AdvanceSearchType | '' = storedParams?.searchType ?? '';

  const page = storedParams?.page ?? 1;

  const toggle = useCallback((flag: boolean) => {
    setOpenCreateModal(flag);
  }, []);

  const isDrag = useCallback((a: boolean) => {
    if (a) dispatch(groupActions.setDropComplete(false));
    setDragging(a);
  }, [dispatch]);

  useEffect(() => {
    setShowNew(false);
    setOpenCreateModal(dragging);
  }, [dragging]);

  useEffect(() => {
    if (!navRequest) {
      if (!storedParams) navigate('/feed', { replace: true });
      return;
    }

    if (isLoading) return;

    if (storedParams && getRequestSignature(navRequest) === getRequestSignature(storedParams)) {
      return;
    }

    if (navRequest.searchType === 'brm' && !navRequest.name && !navRequest.size) {
      navigate(`${FEED_GILROY_ROUTE}?notice=search-cleared`, { replace: true });
      return;
    }
    const isBrm = navRequest.searchType === 'brm';
    const requestToDispatch = isBrm
      ? { ...navRequest, member: undefined, sharedBy: undefined }
      : navRequest;
    dispatch(actions.triggerAdvanceSearch(requestToDispatch));
    if (navRequest.searchType === 'person') {
      dispatch(actions.setAdvanceSearchPeopleForm({
        fullName: navRequest.fullName ?? '',
        currentCo: navRequest.currentCompany ?? '',
        previousCo: navRequest.pastCompany ?? '',
        designation: navRequest.designation ?? '',
        country: navRequest.country ?? '',
        region: navRequest.region ?? '',
      }));
    } else if (navRequest.searchType === 'company') {
      dispatch(actions.setAdvanceSearchCompanyForm({
        name: navRequest.name ?? '',
        industry: navRequest.industry ?? '',
        revenue: navRequest.revenue ?? '',
        country: navRequest.country ?? '',
        region: navRequest.region ?? '',
        size: navRequest.size ?? '',
      }));
    } else if (navRequest.searchType === 'brm') {
      dispatch(actions.setAdvanceSearchBrmForm({
        name: navRequest.name ?? '',
        member: 0,
        size: navRequest.size ?? '',
        sharedBy: 0,
      }));
      const cleanParams = new URLSearchParams(searchParams);
      cleanParams.delete('member');
      cleanParams.delete('sharedBy');
      navigate(`?${cleanParams.toString()}`, { replace: true });
    }
  }, [navRequest, storedParams, isLoading, dispatch, navigate, searchParams]);

  // Derived display values
  const persons = peopleSection?.persons ?? [];
  const companies = companiesSection?.companies ?? [];
  const buyerGroups = buyerGroupsSection?.groups ?? [];

  const totalPersonCount = peopleSection?.count ?? 0;
  const totalCompanyCount = companiesSection?.count ?? 0;
  const totalBuyerGroupCount = buyerGroupsSection?.count ?? 0;


  const isInitialPeopleLoading = !isLoaded && persons.length === 0;
  const isInitialCompanyLoading = !isLoaded && companies.length === 0;
  const isInitialBuyerGroupsLoading = !isLoaded && buyerGroups.length === 0;

  const hasMorePeople = persons.length < totalPersonCount;
  const hasMoreCompanies = companies.length < totalCompanyCount;
  const hasMoreBuyerGroups = buyerGroups.length < totalBuyerGroupCount;

  const personsAll = persons;
  const companiesAll = companies;
  const buyerGroupsAll = buyerGroups;

  const showResultLists = isLoaded || isPaginating;

  const resultsContentKey = useMemo(() => {
    if (!storedParams) return activeSearchType;
    const { page: _page, ...rest } = storedParams;
    return JSON.stringify(rest);
  }, [storedParams, activeSearchType]);

  const fetchMorePeople = () => {
    if (!isLoading && storedParams) {
      dispatch(actions.triggerAdvanceSearch({ ...storedParams, page: page + 1 }));
    }
  };

  const fetchMoreCompanies = () => {
    if (!isLoading && storedParams) {
      dispatch(actions.triggerAdvanceSearch({ ...storedParams, page: page + 1 }));
    }
  };

  const fetchMoreBuyerGroups = () => {
    if (!isLoading && storedParams) {
      dispatch(actions.triggerAdvanceSearch({ ...storedParams, page: page + 1 }));
    }
  };

  const allSearchesSettled = activeSearchType === 'person'
    ? isLoaded && isPersonExtendedLoaded
    : isLoaded;

  const showNoResults = allSearchesSettled
    && !isLoading
    && persons.length === 0
    && companies.length === 0
    && buyerGroups.length === 0;


  return (
    <Wrapper className='advance-search-wrapper'>
      <NavBarPrimary headerClassName="box-shadow no-overflow-on-header">
        <div className="left-side-wrap">
          {enableBackButton && (
            <BackIcon onClick={handleBackClick} />
          )}
          <h1 className="heading">Results</h1>
        </div>
        <div className="right-side-wrap">
          <GlobalSearch iconMode={IsTablet()} />
          <DrawerAlerts />
          <AccountPopOver />
        </div>
      </NavBarPrimary>

      <SubNavBarPrimary headerClassName="search-heading">
        <div className="heading-inner-page tabs-placeholder wrap-slider hidden-scroll">
          <div className="tips-placeholder ml-0">
            <div className="count">
              {/* show people count here */}
              {activeSearchType === 'person' ? (
                <span>
                  People ({totalPersonCount})
                </span>
              ) : activeSearchType === 'company' ? (
                <span>
                  Companies ({totalCompanyCount})
                </span>
              ) : activeSearchType === 'brm' ? (
                <span>
                  Buyer Groups ({totalBuyerGroupCount})
                </span>
              ) : (
                ''
              )}
            </div>
          </div>
          <div className="right-side-sub-header">
            <div className={classNames('view-tabs', { notdisabledTabs: true })}>
              <ViewTabs disabled={false} listView={isListView} />
            </div>
          </div>
        </div>
      </SubNavBarPrimary>

      <div className="container placement-logic">
        <div className="styleing-wrapper fancyBox">
          <DrawerPeopleGroup
            openNewCreate={openCreateModal}
            setOpenNew={toggle}
            showNew={showNew}
            toggleShowNew={(bool: boolean) => setShowNew(bool)}
            noButton
            drag={dragging}
          />

          {activeSearchType === 'person' && (
            <div key={resultsContentKey}>
              {!showResultLists ? (
                <SearchPersonSkeletonBlock inGlobalSearch />
              ) : isListView ? (
                <InfiniteScroll
                  hasMore={hasMorePeople}
                  dataLength={personsAll.length}
                  next={fetchMorePeople}
                  loader={false}
                  className="o-x-hidden o-y-hidden spacer"
                >
                  <PeopleSectionListView
                    heading={peopleSection?.title}
                    peopleCollection={personsAll}
                    loading={isInitialPeopleLoading}
                    peopleCount={persons}
                    count={totalPersonCount}
                    invertFollow
                    inGlobalSearch
                    query=""
                    hideShowAll
                    primarySearchLoaded={isLoaded}
                    extendedSearchLoaded={isPersonExtendedLoaded}
                    isPaginationOverride={isPaginating}
                  />
                </InfiniteScroll>
              ) : (
                <InfiniteScroll
                  hasMore={hasMorePeople}
                  dataLength={personsAll.length}
                  next={fetchMorePeople}
                  loader={false}
                  className="o-x-hidden M-People detailPage"
                >
                  <PeopleSection
                    heading={peopleSection?.title}
                    hideDefaultBackground
                    peopleCollection={personsAll}
                    loading={isInitialPeopleLoading}
                    peopleCount={persons}
                    count={totalPersonCount}
                    query=""
                    inGlobalSearch
                    lazyMountCards={false}
                    invertFollow
                    bothSearchLoading={!isPersonExtendedLoaded || !isLoaded}
                    hideShowAll
                    primarySearchLoaded={isLoaded}
                    extendedSearchLoaded={isPersonExtendedLoaded}
                    isPaginationOverride={isPaginating}
                    isDrag={isDrag}
                  />
                </InfiniteScroll>
              )}
            </div>
          )}

          {activeSearchType === 'company' && (
            <div key={resultsContentKey}>
              {!showResultLists ? (
                <SearchCompanySkeletonBlock inGlobalSearch />
              ) : isListView ? (
                <InfiniteScroll
                  hasMore={hasMoreCompanies}
                  dataLength={companiesAll.length}
                  next={fetchMoreCompanies}
                  loader={false}
                  className="o-x-hidden M-Companies detailPage spacer"
                >
                  <CompaniesSectionListView
                    heading={companiesSection?.title}
                    companiesCollection={companiesAll}
                    loading={isInitialCompanyLoading}
                    companyCount={companies}
                    count={totalCompanyCount}
                    listView={isListView}
                    inGlobalSearch
                    lazyMountCards={false}
                    query=""
                    hideShowAll
                    isPaginationOverride={isPaginating}
                  />
                </InfiniteScroll>
              ) : (
                <InfiniteScroll
                  hasMore={hasMoreCompanies}
                  dataLength={companiesAll.length}
                  next={fetchMoreCompanies}
                  loader={false}
                  className="o-x-hidden M-Companies detailPage spacer"
                >
                  <CompaniesSection
                    heading={companiesSection?.title}
                    hideDefaultBackground
                    companiesCollection={companiesAll}
                    loading={isInitialCompanyLoading}
                    companyCount={companies}
                    count={totalCompanyCount}
                    query=""
                    listView={isListView}
                    inGlobalSearch
                    lazyMountCards={false}
                    hideShowAll
                    isPaginationOverride={isPaginating}
                  />
                </InfiniteScroll>
              )}
            </div>
          )}

          {activeSearchType === 'brm' && (
            <div key={resultsContentKey}>
              {!showResultLists ? (
                <SearchBuyerGroupSkeletonBlock inGlobalSearch />
              ) : isListView ? (
                <InfiniteScroll
                  hasMore={hasMoreBuyerGroups}
                  dataLength={buyerGroupsAll.length}
                  next={fetchMoreBuyerGroups}
                  loader={false}
                  className="o-x-hidden o-y-hidden M-Buyer spacer"
                >
                  <BuyerGroupsSectionListView
                    heading={buyerGroupsSection?.title}
                    buyerGroupsCollection={buyerGroupsAll}
                    loading={isInitialBuyerGroupsLoading}
                    buyerGroupCount={buyerGroups}
                    count={totalBuyerGroupCount}
                    inGlobalSearch
                    lazyMountCards={false}
                    query=""
                    isPaginationOverride={isPaginating}
                  />
                </InfiniteScroll>
              ) : (
                <InfiniteScroll
                  hasMore={hasMoreBuyerGroups}
                  dataLength={buyerGroupsAll.length}
                  next={fetchMoreBuyerGroups}
                  loader={false}
                  className="o-x-hidden o-y-hidden M-Buyer spacer"
                >
                  <BuyerGroupsSection
                    heading={buyerGroupsSection?.title}
                    buyerGroupsCollection={buyerGroupsAll}
                    loading={isInitialBuyerGroupsLoading}
                    buyerGroupCount={buyerGroups}
                    count={totalBuyerGroupCount}
                    inGlobalSearch
                    lazyMountCards={false}
                    query=""
                    isPaginationOverride={isPaginating}
                  />
                </InfiniteScroll>
              )}
            </div>
          )}

          {showNoResults && <SearchNoResultFoundWidget marginTop="250px" />}

        </div>
      </div>
      {showGlobalGilroy
        ? (
          <Gilroy gilroyStates={gilroyStates} gilroyActions={gilroyActions} />
        ) : null}
    </Wrapper>
  );
};

export default AdvanceSearchResults;
